import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  INTERNAL_ROLES_LIMITED_BY_PRACTICING_STATES,
  INTERNAL_ROLE_TO_ABBREVIATED,
} from '~/shared/constants/careTeamRoles'
import sortBy from 'lodash/fp/sortBy'
import { useQuery } from '~/shared/hooks/query'
import Edit from '@mui/icons-material/Edit'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import {
  Autocomplete,
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { getProviders } from '~/app/store/actions/providers'
import { Provider } from '~/entities/provider'
import { pods as podsApi, states as statesApi } from '@fireflyhealth/api'
import type { PodsListResponse, StatesListResponse } from '@fireflyhealth/api'
import EditPracticingStatesModal from './EditPracticingStatesModal'
import { UserAvatar } from '~/shared/ui'
import { useAppDispatch } from '~/app/store/hooks'

// `!important` beats the table's own cell padding rule, which is more specific.
const HEADER_CELL_SX = { pr: '1em !important' }

const NAME_HEADER_CELL_SX = { pl: '14px !important', pr: '1em !important' }

const usePods = () => {
  return useQuery(
    podsApi.listQueryKey(),
    () => podsApi.list().then(r => r.data),
    { staleTime: Infinity },
    {
      error: 'Failed to load care pods',
    },
  )
}

const providerHasEditablePracticingStates = (provider: Provider) =>
  !!provider.provider_fields?.physician_id

const EmpanelmentSettings = () => {
  const [selectedRole, setSelectedRole] = useState<string | null>(null)
  const [selectedState, setSelectedState] = useState<StatesListResponse[number] | null>(null)

  const providers = useSelector(state => state.providers)
  const { data: states, isPending: statesIsLoading } = useQuery(
    statesApi.listQueryKey(),
    () => statesApi.list().then(r => r.data),
    {},
    {
      error: `Unable to fetch states`,
    },
  )
  const { data: podData } = usePods()
  const dispatch = useAppDispatch()

  const [editingPracticingStatesProvider, setEditingPracticingStatesProvider] =
    useState<Provider | null>(null)

  const editPracticingStates = (provider: Provider) => {
    if (providerHasEditablePracticingStates(provider)) {
      setEditingPracticingStatesProvider(provider)
    }
  }

  useEffect(() => {
    if (providers === null) {
      void dispatch(getProviders())
    }
  }, [providers, dispatch])

  if ((providers || []).length === 0) return <CircularProgress />

  // This page should only show active clinicians who are affected by licensing requirements
  const activeProviders = providers.filter(provider => {
    const internalRole = provider.provider_fields?.internal_role
    return (
      provider.is_active &&
      internalRole &&
      INTERNAL_ROLES_LIMITED_BY_PRACTICING_STATES.has(internalRole)
    )
  })
  return (
    <Box sx={{ overflow: 'hidden' }}>
      <Box style={{ padding: '10px', marginLeft: '10px', marginTop: '10px' }}>
        <Typography variant="h6">State Licenses</Typography>
      </Box>

      <Box style={{ padding: '10px', marginLeft: '10px' }} sx={{ display: 'flex' }}>
        <Box style={{ width: 110 }}>
          <Autocomplete
            size="small"
            options={Array.from(INTERNAL_ROLES_LIMITED_BY_PRACTICING_STATES)}
            getOptionLabel={role => INTERNAL_ROLE_TO_ABBREVIATED[role] ?? role}
            onChange={(event, newValue) => {
              setSelectedRole(newValue)
            }}
            renderInput={params => <TextField {...params} size="small" label="Role" />}
          />
        </Box>
        <Box style={{ width: 200, marginLeft: 10 }}>
          <Autocomplete
            size="small"
            options={states ?? []}
            getOptionLabel={state => `${state.name} (${state.abbreviation})`}
            renderInput={params => <TextField {...params} size="small" label="State" />}
            onChange={(event, newValue) => {
              setSelectedState(newValue)
            }}
            disabled={statesIsLoading}
          />
        </Box>
      </Box>
      <EmpanelmentTable
        podData={podData}
        providers={activeProviders}
        selectedState={selectedState}
        selectedRole={selectedRole}
        editPracticingStates={editPracticingStates}
      />
      <EditPracticingStatesModal
        stateOptions={states ?? []}
        loading={statesIsLoading}
        provider={editingPracticingStatesProvider}
        handleCancel={() => setEditingPracticingStatesProvider(null)}
      />
    </Box>
  )
}

const EmpanelmentTable = (props: {
  providers: Provider[]
  selectedState: StatesListResponse[number] | null
  selectedRole: string | null
  editPracticingStates: (provider: Provider) => void
  podData: PodsListResponse | undefined
}) => {
  const HEADER_COLUMNS = [
    { id: 'accordionIcon', label: '', sx: HEADER_CELL_SX },
    { id: 'lastName', label: 'Name', sx: NAME_HEADER_CELL_SX },
    { id: 'role', label: 'Role', sx: HEADER_CELL_SX },
    { id: 'pod', label: 'Care Pod', sx: HEADER_CELL_SX },
    { id: 'practicingStates', label: 'State Licenses', sx: HEADER_CELL_SX },
  ]
  const { providers, selectedState, selectedRole, editPracticingStates } = props

  const filterProviders = (provider: Provider) => {
    const providerHasState = selectedState
      ? (provider.provider_fields?.practicing_states ?? []).find(
          state => state.abbreviation === selectedState.abbreviation,
        )
      : true
    if (!providerHasState) return false
    return selectedRole ? provider.provider_fields?.internal_role === selectedRole : true
  }
  const filteredProviders =
    selectedState || selectedRole ? providers.filter(filterProviders) : providers

  const empanelment = sortBy('first_name', filteredProviders)
  return (
    <TableContainer sx={{ maxHeight: '90vh' }}>
      <Table
        id="empanelment-table"
        stickyHeader
        sx={{
          '& .MuiTableCell-root': {
            borderBottomWidth: '1px',
            borderBottomColor: 'grey.100',
            padding: '4px',
          },
        }}
      >
        <TableHead>
          <TableRow>
            {HEADER_COLUMNS.map(headerColumn => (
              <TableCell sx={headerColumn.sx} key={headerColumn.id}>
                {headerColumn.label}
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {empanelment.map(provider => (
            <EmpanelmentRow
              key={provider.id}
              provider={provider}
              editPracticingStates={editPracticingStates}
              selectedState={selectedState}
              podData={props.podData}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const EmpanelmentRow = (props: {
  provider: Provider
  editPracticingStates: (provider: Provider) => void
  selectedState: StatesListResponse[number] | null
  podData: PodsListResponse | undefined
}) => {
  const { provider, editPracticingStates, selectedState } = props
  const [openStates, setOpenStates] = useState(false)

  const practicingStates = provider.provider_fields?.practicing_states ?? []

  const practicingFilteredStates = selectedState
    ? practicingStates.filter(state => state.abbreviation === selectedState.abbreviation)
    : practicingStates

  let podNames: string[] = []
  const podIds = provider.provider_fields?.pod_providers.map(pp => pp.pod.id)
  if (props.podData && podIds) {
    podNames = props.podData.reduce((previous, current) => {
      if (podIds.includes(current.id)) {
        previous.push(current.name)
      }
      return previous
    }, podNames)
  }

  return (
    <TableRow
      key={provider.id}
      id={`empanelment-provider-${provider.id}`}
      style={{ verticalAlign: 'baseline' }}
    >
      <TableCell width={10}>
        <IconButton aria-label="expand row" size="small" onClick={() => setOpenStates(!openStates)}>
          {openStates ? (
            <KeyboardArrowDownIcon fontSize="small" />
          ) : (
            <KeyboardArrowRightIcon fontSize="small" />
          )}
        </IconButton>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <UserAvatar id={provider.id} size="small" />
          <Typography>
            {`${provider.first_name} ${provider.last_name}`}
            {provider.provider_fields?.title?.trim()
              ? ` (${provider.provider_fields.title.trim()})`
              : ''}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography>
          {provider.provider_fields?.internal_role &&
            INTERNAL_ROLE_TO_ABBREVIATED[provider.provider_fields.internal_role]}
        </Typography>
      </TableCell>
      <TableCell>
        {podNames.length ? <Typography>{podNames.join(', ')}</Typography> : null}
      </TableCell>
      <TableCell>
        <Box>
          <Typography>{practicingStates.length}</Typography>
        </Box>
        <Collapse in={openStates} timeout="auto" unmountOnExit>
          {practicingFilteredStates.map(practicingState => (
            <TableRow key={practicingState.abbreviation}>
              <TableCell style={{ borderBottom: 'none', paddingTop: '8px', paddingBottom: '8px' }}>
                <Typography>{practicingState.abbreviation}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </Collapse>
      </TableCell>
      <TableCell style={{ textAlign: 'end' }}>
        {providerHasEditablePracticingStates(provider) && (
          <IconButton onClick={() => editPracticingStates(provider)}>
            <Box sx={{ color: 'grey.500' }}>
              <Edit />
            </Box>
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  )
}
export default EmpanelmentSettings
